// Fonte única de verdade para CNPJ (numérico legado + alfanumérico da RFB).
// Algoritmo de validação espelha a implementação oficial da Receita Federal usada
// no backend e no skillhub-web: dígitos verificadores calculados com (charCodeAt - 48),
// de modo que letras (A=17, ..., Z=42) entram no cálculo. Sem biblioteca externa.

const REPEATED_REGEX = /^(\d)\1*$/
const CNPJ_FORMAT_REGEX = /^[A-Z\d]{12}\d{2}$/

/**
 * Remove a máscara, mantém apenas [A-Z0-9] e força uppercase.
 * Use antes de validar e antes de enviar o valor ao backend.
 */
export const normalizeCnpj = (value: string): string =>
  (value ?? '').replace(/[^A-Za-z0-9]/g, '').toUpperCase()

/**
 * Aplica a máscara progressiva AA.AAA.AAA/AAAA-00 sobre o valor (normalizado internamente).
 * Funciona durante a digitação (formata o que houver até 14 caracteres).
 */
export const formatCnpj = (value: string): string => {
  const raw = normalizeCnpj(value).slice(0, 14)
  let result = raw.slice(0, 2)
  if (raw.length > 2) result += '.' + raw.slice(2, 5)
  if (raw.length > 5) result += '.' + raw.slice(5, 8)
  if (raw.length > 8) result += '/' + raw.slice(8, 12)
  if (raw.length > 12) result += '-' + raw.slice(12, 14)
  return result
}

const calcCheckDigit = (base: string): number => {
  let sum = 0
  let pos = base.length - 7
  for (let i = 0; i < base.length; i++) {
    sum += (base.charCodeAt(i) - 48) * pos--
    if (pos < 2) pos = 9
  }
  const remainder = sum % 11
  return remainder < 2 ? 0 : 11 - remainder
}

/**
 * Valida CNPJ numérico (legado) e alfanumérico (RFB). Aceita valor com ou sem máscara.
 */
export const isValidCnpj = (value: string): boolean => {
  const cnpj = normalizeCnpj(value)
  if (!CNPJ_FORMAT_REGEX.test(cnpj)) return false
  if (REPEATED_REGEX.test(cnpj)) return false

  const digits = cnpj.slice(12)
  const firstDigit = calcCheckDigit(cnpj.slice(0, 12))
  if (firstDigit !== parseInt(digits[0])) return false

  const secondDigit = calcCheckDigit(cnpj.slice(0, 13))
  return secondDigit === parseInt(digits[1])
}
